import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "../utils/focusTrap";
import {
  IoSparklesOutline,
  IoAddCircleOutline,
  IoHeartOutline,
  IoNotificationsOutline,
  IoFlameOutline,
} from "react-icons/io5";

const STORAGE_KEY = "virsa_onboarding_seen_v1";

const SLIDES = [
  {
    icon: IoSparklesOutline,
    title: "Welcome to GatherYou",
    body: "A place to share and discover Punjabi fashion and embroidery designs — suits, jutti, phulkari, bridal work, and more.",
  },
  {
    icon: IoAddCircleOutline,
    title: "Share your own designs",
    body: "Tap the + button any time to post photos of your work, along with an optional embroidery file others can download.",
  },
  {
    icon: IoHeartOutline,
    title: "Like, comment, save",
    body: "Show love on designs you like, leave a comment, or bookmark them to your Saved page for later — you can even organize saves into folders.",
  },
  {
    icon: IoNotificationsOutline,
    title: "Stay in the loop",
    body: "Follow designers you like and turn on notifications (in Profile settings) so you never miss a like, comment, or new follower.",
  },
  {
    icon: IoFlameOutline,
    title: "Explore what's trending",
    body: "Check the Trending tab for the week's most-loved designs, or tap any hashtag to see everything posted with it.",
  },
];

// Shown once, the first time someone opens the app — a lightweight
// slideshow instead of DOM-anchored tooltips, so it works the same
// regardless of screen size or which page happens to load first.
export default function OnboardingModal() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const cardRef = useRef(null);
  useFocusTrap(cardRef, visible);

  useEffect(() => {
    let seen = true;
    try {
      seen = localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      // localStorage unavailable — just don't show it rather than risk
      // showing it on every visit.
    }
    if (!seen) setVisible(true);
  }, []);

  // Every other overlay in the app (FollowListModal, ReportModal,
  // ImageCropModal, ImageLightbox, NotificationBell) locks background
  // scroll while open — this one didn't, so the feed behind it could still
  // scroll under a first-time visitor's thumb.
  useEffect(() => {
    if (!visible) return;

    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") dismiss();
      else if (e.key === "ArrowRight") setStep(s => (s === SLIDES.length - 1 ? s : s + 1));
      else if (e.key === "ArrowLeft") setStep(s => (s === 0 ? s : s - 1));
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  function dismiss() {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // Nothing to do — worst case it shows again next visit.
    }
  }

  if (!visible) return null;

  const slide = SLIDES[step];
  const Icon = slide.icon;
  const isLast = step === SLIDES.length - 1;

  return createPortal(
    <div style={backdrop} onClick={dismiss}>
      <div style={card} ref={cardRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={slide.title} onClick={e => e.stopPropagation()}>
        <button style={skipBtn} onClick={dismiss}>Skip</button>

        <div style={iconWrap}>
          <Icon size={40} />
        </div>

        <h2 style={title}>{slide.title}</h2>
        <p style={body}>{slide.body}</p>

        <div style={dotsRow}>
          {SLIDES.map((_, i) => (
            <span key={i} style={i === step ? dotActive : dot} />
          ))}
        </div>

        <button
          style={primaryBtn}
          onClick={() => (isLast ? dismiss() : setStep(s => s + 1))}
        >
          {isLast ? "Get started" : "Next"}
        </button>
      </div>
    </div>,
    document.body
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(20, 14, 8, 0.6)",
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

const card = {
  position: "relative",
  width: "100%",
  maxWidth: 360,
  background: "var(--bg-surface)",
  borderRadius: 20,
  padding: "36px 26px 26px",
  textAlign: "center",
  boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
};

const skipBtn = {
  position: "absolute",
  top: 14,
  right: 16,
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const iconWrap = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 72,
  height: 72,
  borderRadius: "50%",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)",
  margin: "0 auto 18px",
};

const title = {
  fontFamily: "'Fraunces', serif",
  fontSize: 20,
  color: "var(--text-heading)",
  margin: "0 0 10px",
};

const body = {
  fontSize: 13.5,
  color: "var(--text-body)",
  lineHeight: 1.6,
  margin: "0 0 22px",
};

const dotsRow = {
  display: "flex",
  justifyContent: "center",
  gap: 6,
  marginBottom: 22,
};

const dot = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "var(--border-input)",
};

const dotActive = {
  ...dot,
  width: 18,
  background: "var(--accent-primary)",
};

const primaryBtn = {
  width: "100%",
  padding: "13px 14px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontSize: 14.5,
  fontWeight: 700,
  cursor: "pointer",
};
